import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { concatMap, last, takeWhile } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { ApiResponse } from '../../shared/types';
import {
  CompanyFlag,
  CompanyFlagUpdatePayload,
  FlagSummary,
  GlobalFlagUpdatePayload,
  LocationFlagChange,
  LocationFlagUpdatePayload,
} from '../models/feature-flag.model';

/**
 * CWS-1805: super-admin feature flags. Global switch + company default on the
 * flags page, per-company and per-location overrides on the company page.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly api = inject(ApiService);

  private companyPath(companyId: string, key?: string): string {
    const base = `admin/super-admin/companies/${companyId}/feature-flags`;
    return key ? `${base}/${key}` : base;
  }

  listFlags(): Observable<ApiResponse<FlagSummary[]>> {
    return this.api.getWithSuperAdminToken<FlagSummary[]>('admin/super-admin/feature-flags');
  }

  updateFlag(
    key: string,
    payload: GlobalFlagUpdatePayload
  ): Observable<ApiResponse<FlagSummary[]>> {
    return this.api.putWithSuperAdminToken<FlagSummary[]>(
      `admin/super-admin/feature-flags/${key}`,
      payload
    );
  }

  getCompanyFlags(companyId: string): Observable<ApiResponse<CompanyFlag[]>> {
    return this.api.getWithSuperAdminToken<CompanyFlag[]>(this.companyPath(companyId));
  }

  updateCompanyFlag(
    companyId: string,
    key: string,
    payload: CompanyFlagUpdatePayload
  ): Observable<ApiResponse<CompanyFlag[]>> {
    return this.api.putWithSuperAdminToken<CompanyFlag[]>(
      this.companyPath(companyId, key),
      payload
    );
  }

  updateLocationFlag(
    companyId: string,
    key: string,
    locationId: string,
    payload: LocationFlagUpdatePayload
  ): Observable<ApiResponse<CompanyFlag[]>> {
    return this.api.putWithSuperAdminToken<CompanyFlag[]>(
      `${this.companyPath(companyId, key)}/locations/${locationId}`,
      payload
    );
  }

  /**
   * Several location changes, one request at a time. Stops at the first refusal
   * and emits that response, else the last one (each carries every flag).
   * `changes` must not be empty.
   */
  updateLocationFlags(
    companyId: string,
    key: string,
    changes: LocationFlagChange[]
  ): Observable<ApiResponse<CompanyFlag[]>> {
    return from(changes).pipe(
      concatMap(change =>
        this.updateLocationFlag(companyId, key, change.locationId, change.payload)
      ),
      takeWhile(res => res.status !== false, true),
      last()
    );
  }
}
